import _uniq                            from 'lodash/uniq';
import _get                             from 'lodash/get';
import _uniqueId                        from 'lodash/uniqueId';
import _uniqBy                          from 'lodash/uniqBy';
import _groupBy                         from 'lodash/groupBy';
import { AbstractResolvableCollection } from '@mathquis/modelx-resolvables';
import ConnectorResults                 from '@mathquis/modelx/lib/types/connectorResults';
import { ToResolveDictionary }          from '@widesk-core/mixins/ApiModelWhenIsLoaded';
import ApiModel                         from '@widesk-core/models/ApiModel';
import utc                              from 'dayjs/plugin/utc';
import dayjs                            from 'dayjs';
import { computed }                     from 'mobx';
import { when }                         from 'mobx';
import { override }                     from 'mobx';
import { action }                       from 'mobx';
import { ICollectionOptions }           from '@mathquis/modelx/lib/types/collection';
import _camelCase                       from 'lodash/camelCase';
import ucFirst                          from '@widesk-core/tools/ucFirst';
import { getLocale }                    from '@widesk-core/locale';
import { getLanguages }                 from '@widesk-core/languages';

dayjs.extend(utc);

export type CollectionSorts = Record<string, 'asc' | 'desc'>;

declare type iteratee<T, V> = (model: T, index?: number) => V;
declare type ResolvableKey<M extends ApiModel> = {
	[K in keyof M]: M[K] extends ApiModel ? K : never;
}[Extract<keyof M, string>];

const MAX_RESULTS = 1000; // Permet de déclencher une erreur si la collection contient trop de données

export default class ApiCollection<M extends ApiModel> extends AbstractResolvableCollection<M> {
	protected _filters: Partial<ModelFilters<M>> = {};
	protected _customHeaders: RequestCustomHeaders = {};
	protected _requiredFilters: ModelFilterName<M>[] = [];
	protected _sorts: CollectionSorts = {};

	protected setCustomHeaders(headers: RequestCustomHeaders): this {
		this._customHeaders = headers;

		return this;
	}

	public get customHeaders(): RequestCustomHeaders {
		return this._customHeaders;
	}

	public setLocale(value: LocaleHeaderValue): this {
		value = value === true ? getLocale() : value;

		if (value !== undefined) {
			return this.setCustomHeaders({
				...this.customHeaders,
				'X-Locale': value || undefined,
				'X-Partial-Translation': value ? true : undefined,
			});
		}

		return this;
	}

	public hasLocale(locale: LanguageKey | '*' | true): boolean {
		locale = locale === true ? getLocale() : locale;
		const localeHeaderValues = this.customHeaders['X-Locale'];

		if (Array.isArray(localeHeaderValues)) {
			return locale === '*'
				? getLanguages().every(lang => localeHeaderValues.includes(lang))
				: localeHeaderValues.includes(locale);
		}

		return localeHeaderValues === locale;
	}

	public setIncludeFiles(value: boolean | undefined): this {
		if (value !== undefined) {
			return this.setCustomHeaders({
				...this.customHeaders,
				'X-Include-Files': value ? true : undefined,
			});
		}

		return this;
	}

	public get hasIncludeFiles(): boolean {
		const customHeaderKeys = Object.keys(this.customHeaders) as RequestCustomHeaderKey[];
		return customHeaderKeys.some(customHeaderKey => customHeaderKey === 'X-Include-Files');
	}

	protected initialize(options: ICollectionOptions) {
		super.initialize(options);

		// Si une collection est initialisée avec des models qui sont tous "isLoaded" on passe la collection en "isLoaded".
		if (!this.isLoaded && !this.isLoading && this.length && this.every(m => m.isLoaded)) this.setIsLoaded(true);
	}

	protected checkRequiredFilter(value: any) {
		return value !== undefined && value !== null && value !== '';
	}

	protected checkRequiredFilters() {
		for (const filter of this._requiredFilters) {
			if (
				!this.checkRequiredFilter(this._filters[filter])
				|| (
					typeof this._filters[filter] === 'object'
					&& !Object.values(this._filters[filter] || {}).some(this.checkRequiredFilter)
				)
			) {
				console.warn(`Filter "${filter}" empty for request "${this.path}"`);

				return false;
			}
		}

		return true;
	}

	@computed get ids(): id[] {
		return this.map(m => m.id);
	}

	@computed get urns(): Urn[] {
		return this.map(m => m.urn);
	}

	@computed get filters(): Partial<ModelFilters<M>> {
		return this._filters;
	}

	@computed get search(): ModelFilters<M>['search'] | undefined {
		return this.filters.search;
	}

	@computed get modelName(): string {
		const model = this.model as { urnData?: { resource: string }, name: string };
		return ucFirst(_camelCase(model['urnData']?.['resource'] || model.name));
	}

	public map<V>(iteratee: iteratee<M, V>): V[] {
		return super.map(iteratee);
	}

	public clear() {
		this.setIsLoaded(false);
		this.clearFilters().clearSorts();
		return super.clear();
	}

	public clearFilters() {
		this._filters = {};
		return this;
	}

	public clearModels() {
		return super.clear();
	}

	public clearSorts() {
		this._sorts = {};
		return this;
	}

	public setRequiredFilter<FilterName extends ModelFilterName<M>>(name: FilterName, value: ModelFilters<M>[FilterName]) {
		this._requiredFilters.push(name);
		return this.setFilter(name, value);
	}

	public setFilter<FilterName extends ModelFilterName<M>>(name: FilterName, value?: ModelFilters<M>[FilterName]) {
		const DATE_FORMAT = 'YYYY-MM-DD[T]LTS';

		if (typeof value === 'undefined') {
			delete this._filters[name];
		} else if (value instanceof dayjs) {
			this._filters[name] = (value as unknown as Dayjs).utc().format(DATE_FORMAT) as never;
		} else if (Array.isArray(value)) {
			const arr = value as unknown[];

			arr.forEach((v, idx) => {
				if (v instanceof dayjs) {
					arr[idx] = (v as unknown as Dayjs).utc().format(DATE_FORMAT);
				}
			});

			// On supprime les éléments en doublon
			this._filters[name] = _uniq(arr) as never;
		} else {
			this._filters[name] = value;
		}

		return this;
	}

	public addFilters(filters: ModelFilters<M>): this {
		Object.keys(filters || {}).forEach((name: any) => this.setFilter(name, (filters as any)[name]));
		return this;
	}

	public setFilters(filters: ModelFilters<M>): this {
		this._filters = {}; // Suppression de tous les filtres
		this.addFilters(filters);
		return this;
	}

	public setSort(name?: keyof ModelSorts<M>, way: SortWay = 'asc'): this {
		const key = `order[${name as string}]`;

		if (typeof name === 'undefined') {
			delete this._sorts[key];
		} else {
			this._sorts[key] = way;
		}

		return this;
	}

	@action setIsLoaded(value = true) {
		this.isLoaded = value;

		return this;
	}

	@action setIsLoading(value = true) {
		this['_pendingRequestCount'] = value ? 1 : 0;

		return this;
	}

	public uniqueId = _uniqueId();
	public toResolveDictionary: ToResolveDictionary = {};

	/**
	 * Supprime en UNE requête tout ce que matchent les filtres courants (`DELETE <path>?<filtres>`).
	 *
	 * Ne passe PAS par `list()`, malgré les filtres en commun : l'API ne répond pas une collection à
	 * un `DELETE`, or `Collection.onListSuccess` fait `results.items.map(...)`. Sans `member` dans la
	 * réponse (204, ou 200 sans corps de collection), `ConnectorResults.items` vaut `undefined` et ce
	 * `.map` jette — jet que `Collection.list` convertit en REJET de sa promesse. L'appelant voyait
	 * donc échouer une suppression pourtant effective côté serveur.
	 *
	 * Les models déjà chargés dans la collection ne sont pas retirés : la suppression porte sur les
	 * FILTRES, pas sur le contenu local, et l'appelant recharge en général derrière.
	 */
	public async destroy() {
		if (!this.checkRequiredFilters()) return this;

		await (this.model.connector as any).list(this, this.prepareListOptions({
			method: 'DELETE',
			headers: { ...this.customHeaders },
		}));

		return this;
	}

	public async patch(
		getAttributes: (m: M) => Record<string, unknown>,
		options: ConnectorSaveOptions = {},
	) {
		return this.save(options, getAttributes);
	}

	public async save(
		options: ConnectorSaveOptions = {},
		getAttributes?: (m: M) => Record<string, unknown>,
	) {
		if (!this.length) return;

		const method = options.method || (getAttributes ? 'patch' : 'post');

		return this.requestSave(options, {
			method,
			// Le suffixe `batch` ne concerne que la création (`POST`) : l'API attend un `PATCH`/`PUT`
			// batch directement sur le path de la collection (confirmé par l'équipe API).
			batchPath: method === 'post',
			patchAttributes: getAttributes
				? { items: this.toBatchItems(getAttributes) }
				: this.toBatchItems(),
		});
	}

	/**
	 * Requête batch "brute" : toujours sur `<path>batch` quelle que soit la méthode HTTP, et avec
	 * un payload à plat (`[{ id, ... }]`) là où `save()` l'enveloppe dans `{ items: [...] }`.
	 * Contrairement à `save()`, aucune méthode HTTP par défaut : passez `options.method`.
	 */
	public async batch(
		options: ConnectorSaveOptions = {},
		getAttributes?: (m: M) => Record<string, unknown>,
	) {
		if (!this.length) return;

		return this.requestSave(options, {
			method: options.method,
			batchPath: true,
			patchAttributes: this.toBatchItems(getAttributes),
		});
	}

	/**
	 * Partie commune à `save()` et `batch()` : headers, envoi au connector puis réapplication
	 * du résultat sur les models. Seuls le path, la méthode et la forme du payload varient.
	 */
	private async requestSave(options: ConnectorSaveOptions, request: {
		method: ConnectorSaveOptions['method'];
		batchPath: boolean; // Ajoute le suffixe `batch` au path de la collection
		patchAttributes: unknown;
	}) {
		this.warnDeprecatedTranslations(options);

		const newModel = new this.model();

		const connectorResult = await (this.model.connector as any).save(newModel, {
			path: request.batchPath ? `${newModel.path}batch` : newModel.path,
			method: request.method,
			...options,
			headers: {
				...this.customHeaders,
				...options?.headers,
			},
			patchAttributes: request.patchAttributes,
		});

		this.applySaveResult(connectorResult, request.method);
	}

	/**
	 * Les attributs de chaque model, sous forme de liste envoyable en batch.
	 * Avec `getAttributes` on n'envoie que les attributs retournés, plus l'id pour cibler le model.
	 */
	private toBatchItems(getAttributes?: (m: M) => Record<string, unknown>) {
		return getAttributes
			? this.models.map(m => ({ id: m.id || undefined, ...getAttributes(m) }))
			: this.models.map(m => m.attributes);
	}

	/**
	 * Réapplique sur chaque model de la collection les attributs renvoyés par la requête batch.
	 */
	private applySaveResult(connectorResult: unknown, method: ConnectorSaveOptions['method']) {
		const isPatch = method === 'patch';
		const attributes = _get(connectorResult, isPatch ? 'attributes.member' : 'attributes', []);

		this.models.forEach((m, idx) => {
			// En `PATCH` l'ordre des items renvoyés n'est pas garanti : on matche par id.
			const modelAttributes = isPatch
				? attributes.find((item: any) => item.id === m.id)
				: attributes.at(idx);

			if (!modelAttributes) return;

			if (isPatch) {
				(m as any).onSaveSuccess({ modelAttributes });
			} else {
				m.set(modelAttributes);
			}
		});
	}

	private warnDeprecatedTranslations(options?: ConnectorSaveOptions | ConnectorListOptions) {
		if (options?.translations) {
			console.warn(`Collection <${this.modelName}> : L'attribut 'translations' est déprécié, Utilisez apiCollection.setLocale().`);
		}
	}

	public async listBy<FilterName extends ModelFilterName<M>>(
		values: string | string[],
		filterName = 'id' as FilterName,
		options: ConnectorListOptions = {},
	) {
		const arrValues = Array.isArray(values) ? values : [values];

		// On supprime les valeurs "undefined"
		let cleanValues = [...new Set(arrValues as never)].filter(v => typeof v !== 'undefined' && v !== '');

		if (filterName === 'id') {
			// On supprime les ids "0"
			cleanValues = cleanValues.filter(id => id);
		}

		this.setFilter(filterName, cleanValues as unknown as ModelFilters<M>[FilterName]);

		if (cleanValues.length) {
			await this.list(options);
		} else {
			this.clearModels();
		}

		return this;
	}

	// Pour les resolvables
	public async listById(identifiers: id[]): Promise<this> {
		await this.listBy(identifiers, 'id', { params: { itemsPerPage: identifiers.length } });

		// Si un id n'a pas été trouvé dans la requête list
		if (__LOCAL__) {
			const notFoundIdentifiers = identifiers.filter(id => !this.ids.includes(id));

			if (notFoundIdentifiers.length) {
				// On affiche un message avec la liste des entités et identifiants non trouvés
				const notFoundMessage = `Resolvable - listById - ${this.model.name} not found (ID : ${notFoundIdentifiers.join(', ')})`;
				console.error(notFoundMessage, this.ids);
			}
		}

		return this;
	}

	private _abortController?: AbortController;

	protected prepareListOptions(options: any) {
		this._abortController = new AbortController();

		const superOptions = {
			signal: this._abortController.signal,
			...options,
			params: { ...this._filters, ...this._sorts, ...options.params },
		};

		// Lorsqu'on filtre sur l'id on supprime la pagination
		if (superOptions.params.id) {
			const ids = Array.isArray(superOptions.params.id) ? superOptions.params.id : [superOptions.params.id];
			const itemsPerPage = superOptions.params.itemsPerPage || -1;

			if (ids.length <= itemsPerPage) { // Seulement s'il y a moins d'IDs en paramètre que la pagination en cours
				superOptions.params.itemsPerPage = ids.length;
				superOptions.params.page = undefined;
				superOptions.params.partial = true; // Évite une requête COUNT du total sur l'API
			}
		}

		return super.prepareListOptions(superOptions);
	}

	/**
	 * La collection doit déjà être "isLoaded" lorsqu'on appelle cette fonction
	 */
	public async whenIsLoaded(iterator?: (m: M) => ApiModel | ApiModel[]) {
		if (!iterator) {
			return when(() => this.isLoaded);
		}

		const toLoadModels = this.map(model => iterator(model)).flat();
		await Promise.all(toLoadModels.map(toLoadModel => toLoadModel.whenIsLoaded()));
	}

	@override onListSuccess(results: ConnectorResults, options: object) {
		super.onListSuccess(results, options);

		const typedOptions = options as ConnectorListOptions;
		const maxResults = typedOptions.maxResults || MAX_RESULTS;

		if (this.length > maxResults) {
			const message = `La collection "${this.path}" contient plus de ${maxResults} résultats. (${this.length} résultats)`;
			console.error(message);
			throw Error(message);
		}
	}

	@override onListError(results: any) {
		if (results.code === 'ERR_CANCELED') {
			console.warn('Requête annulée', this.path);
			return;
		}
		super.onListError(results);
	}

	public abort() { // Permet d'interrompre une requête en cours
		this._abortController?.abort();
	}

	public async list(options?: ConnectorListOptions) {
		if (this.checkRequiredFilters()) {
			this.warnDeprecatedTranslations(options);

			return super.list({
				...options,
				headers: {
					...this.customHeaders,
					...options?.headers,
				},
			});
		}

		return this;
	}

	public distinctBy<V>(iteratee: iteratee<M, V>): M[] {
		return _uniqBy(this.models, iteratee);
	}

	public groupBy<V>(iteratee: iteratee<M, V>): Record<string, M[]> {
		return _groupBy(this.models, iteratee);
	}

	public findBy<T extends Extract<keyof M, string>>(property: T, value: M[T]) {
		return this.models.find(m => m[property] === value);
	}

	public findByResolvableId(resolvableProperty: ResolvableKey<M>, value: id) {
		return this.models.find(m => m.getId(resolvableProperty) === value);
	}

	public findByResolvableUrn(resolvableProperty: ResolvableKey<M>, value: Urn) {
		return this.models.find(m => m.getUrn(resolvableProperty) === value);
	}

	public filterBy<T extends Extract<keyof M, string>>(property: T, value: M[T]) {
		return this.models.filter(m => m[property] === value);
	}

	public filterByResolvableId(resolvableProperty: ResolvableKey<M>, value: id) {
		return this.models.filter(m => m.getId(resolvableProperty) === value);
	}

	public filterByResolvableUrn(resolvableProperty: ResolvableKey<M>, value: Urn) {
		return this.models.filter(m => m.getUrn(resolvableProperty) === value);
	}

	public filterByIncluded<T extends Extract<keyof M, string>>(property: T, values: M[T][]) {
		return this.models.filter(m => values.includes(m[property]));
	}

	public filterByIncludedResolvableId(resolvableProperty: ResolvableKey<M>, values: id[]) {
		return this.models.filter(m => values.includes(m.getId(resolvableProperty)));
	}

	public filterByIncludedResolvableUrn(resolvableProperty: ResolvableKey<M>, values: Urn[]) {
		return this.models.filter(m => values.includes(m.getUrn(resolvableProperty)));
	}

	public filterByResolvableProp<
		R extends ResolvableKey<M>, E extends Extract<keyof M[R], string>
	>(resolvable: R, resolvableProperty: E, value: M[R][E]) {
		return this.models.filter(m => m[resolvable][resolvableProperty] === value);
	}

	public mapBy<T extends Extract<keyof M, string>>(property: T): M[T][] {
		return this.models.map(m => m[property]);
	}
}